import React, { useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { FinanceDepositWayGroupListItem, PaginationRes } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import CoreTable from '../../default/present/CoreTable'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { useGetDataByPayload, useReload, useRequestFromSearch, usePaginationClickAndChangeUrl } from '../../../utils/default/ComplexFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { createDefaultPaginationData, createTableData, formatMoney } from '../../../utils/default/TableHelper'
import depositTypeName from '../../../constants/default/depositTypeName'
import TableStatus from '../../default/present/TableStatus'
import FinanceDepositWayGroupButtons from './FinanceDepositWayGroupButtons'
import CustomThemeType from '../../../themes/admin/CustomThemeType'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import { searchToRequest, RequestType } from '../../../views/admin/finance/FinanceDepositWayGroupPage'
import allRoute from '../../../components/admin/route/route'
import { isUndefined, omitBy } from '@golden/utils'
import DateTime from '../../default/present/DateTime'

const useStyles = makeStyles((theme: CustomThemeType) => ({
  blueText: {
    color: theme.custom.palette.blue.main
  }
}))

interface RowType {
  id: number
  name: string
  method: string
  minCash: string
  maxCash: string
  supportAccounts: React.ReactElement
  supportWays: React.ReactElement | string
  status: React.ReactElement | string
  operator: string
  operatedAt: React.ReactElement | string
  functions: React.ReactElement
}

const FinanceDepositWayGroupTable: React.FC = () => {
  const request = useRequestFromSearch({ searchToRequest })
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const { reloadFlag, reload } = useReload()
  const pageFlow = usePageFlow(true)
  const gdk = useGDK()
  const writable = useChecker()
  const { t } = useT()

  const [list, setList] = useState<PaginationRes<FinanceDepositWayGroupListItem[]>>(createDefaultPaginationData([]))

  const payload = useMemo(() => {
    const converted = { ...request, page: request?.page ?? 1, is_active: request?.is_active === 2 ? undefined : request?.is_active }
    return omitBy(converted, isUndefined) as unknown as RequestType
  }, [request])
  useGetDataByPayload({
    payload,
    gdkFunc: (payload) => gdk.finance.getDepositWayGroupList(payload),
    gdkFuncDependencies: [gdk, reloadFlag],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: PaginationRes<FinanceDepositWayGroupListItem[]>) => {
      setList(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError
  })

  const tableClasses = useMemo(() => {
    return {
      head: commonClasses.pinkTableHead,
      row: commonClasses.tableRow,
      cellHead: commonClasses.tableCellHead
    }
  }, [commonClasses])

  const rows = useMemo(() => {
    return list.data.map((item) => {
      return {
        id: item.id,
        name: item.name,
        method: t(depositTypeName[item.method]),
        minCash: formatMoney(item.cash_min),
        maxCash: formatMoney(item.cash_max),
        supportAccounts: item.support_accounts.length
          ? (<Box>
              { item.support_accounts.map((el) => {
                return (<div className={classes.blueText} key={`support-accounts-${el.id}`}>{ el.account }</div>)
              })}
            </Box>)
          : '-',
        supportWays: item.support_ways.length
          ? (<Box>
              { item.support_ways.map((el) => {
                return (<div className={classes.blueText} key={`support-ways-${el.id}`}>{ el.name }</div>)
              })}
            </Box>)
          : '-',
        status: (
          <React.Fragment>
            <TableStatus status={item.is_active} />
          </React.Fragment>
        ),
        operator: item.operator,
        operatedAt: item.operated_at ? (<DateTime time={item.operated_at} />) : (<p></p>),
        functions: (
          <FinanceDepositWayGroupButtons
            item={item}
            reload={reload}
          />
        )
      } as RowType
    })
  }, [t, list, classes, reload])
  const data = useMemo(() => {
    return createTableData(
      {
        id: {
          label: '',
          value: 'id'
        },
        name: {
          label: t('common.depositWayGroupName'),
          value: 'name',
          align: 'center'
        },
        method: {
          label: t('common.depositWay'),
          value: 'method',
          align: 'center'
        },
        minCash: {
          label: t('common.minDepositMoney'),
          value: 'minCash',
          align: 'center'
        },
        maxCash: {
          label: t('common.maxDepositMoney'),
          value: 'maxCash',
          align: 'center'
        },
        supportAccounts: {
          label: t('common.supportAccounts'),
          value: 'supportAccounts',
          align: 'center'
        },
        supportWays: {
          label: t('common.supportWays'),
          value: 'supportWays',
          align: 'center',
          width: 180
        },
        status: {
          label: t('common.status'),
          value: 'status',
          align: 'center',
          width: 120
        },
        operator: {
          label: t('common.updateBy'),
          value: 'operator',
          align: 'center'
        },
        operatedAt: {
          label: t('common.updateAt'),
          value: 'operatedAt',
          align: 'center'
        },
        functions: {
          label: t('common.function'),
          value: 'functions',
          align: 'center'
        }
      },
      writable
        ? [
            'name',
            'method',
            'minCash',
            'maxCash',
            'supportAccounts',
            'supportWays',
            'status',
            'operator',
            'operatedAt',
            'functions'
          ]
        : [
            'name',
            'method',
            'minCash',
            'maxCash',
            'supportAccounts',
            'supportWays',
            'status',
            'operator',
            'operatedAt'
          ],
      rows,
      'id'
    )
  }, [t, rows, writable])

  const handlePagination = usePaginationClickAndChangeUrl({
    request,
    encodePath: allRoute.financeDepositWay.encodePath,
    pageFlow
  })

  return (
    <LoadingAndErrorFrame { ...pageFlow.status }>
      <CoreTable
        classes={tableClasses}
        data={data}
        total={list.total}
        showPagination
        page={request?.page ?? 1}
        onChangePage={handlePagination}
        loading={pageFlow.status.loading}
      />
    </LoadingAndErrorFrame>
  )
}

export default FinanceDepositWayGroupTable
